// EstudiantesCatalog.jsx - Catálogo de estudiantes (BD Estudiante) con CRUD y modal de alta/edición
import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import { normalizeValue } from '../../../../../utils/Texts';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from './EstudiantesModal';
import EstudiantesTabs from './EstudiantesTabs';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Estudiantes";
const endpoint = ENDPOINTS.estudiante;
const endpointById = ENDPOINTS.estudianteById;
const modelo = Models.estudiante;

const maps = {
  mapCreate: (f) => ({
    idEmpleado: Number(f.idEmpleado) || 0,
    avatarUrl: f.avatarUrl,
    nickname: f.nickname,
    modenas: f.modenas,
    monedasAcumuladas: f.monedasAcumuladas,
    monedasGastadas: 0, // Siempre se envía 0
    activo: true,
    notificacionesCursos: f.notificacionesCursos ?? false,
  }),
  mapUpdate: (id, f) => ({
    idEstudiante: id,
    idEmpleado: Number(f.idEmpleado) || 0,
    avatarUrl: f.avatarUrl,
    nickname: f.nickname,
    modenas: f.modenas,
    monedasAcumuladas: f.monedasAcumuladas,
    monedasGastadas: f.monedasGastadas, // Se respeta el valor de BD
    activo: true,
    notificacionesCursos: f.notificacionesCursos ?? false,
  }),
};

export default function EstudiantesCatalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]); // Estudiantes
  const [empleados, setEmpleados] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editingRowId, setEditingRowId] = useState(null);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const editorRef = useRef(null);

  // Cargar estudiantes desde BD
  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoint);
      const estudiantes = (res.data || []).map((x) => modelo(x));
      setRows(estudiantes);
    } catch (error) {
      console.error('Error al cargar estudiantes:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar empleados y personas para el modal (copiados de módulo General)
  const loadEmpleadosPersonas = async () => {
    try {
      const [resEmp, resPer] = await Promise.all([
        axios.get(ENDPOINTS.empleados),
        axios.get(ENDPOINTS.personas),
      ]);
      setEmpleados((resEmp.data || []).map((x) => Models.empleado(x)));
      setPersonas((resPer.data || []).map((x) => Models.personas(x)));
    } catch (error) {
      console.error('Error al cargar empleados/personas para estudiantes:', error);
    }
  };

  useEffect(() => {
    loadData();
    loadEmpleadosPersonas();
  }, []);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  // Helper: nombre completo de empleado
  const getEmpleadoNombre = (idEmpleado) => {
    const emp = empleados.find((e) => e.idEmpleado === idEmpleado);
    if (!emp) return `Empleado ${idEmpleado}`;
    const per = personas.find((p) => p.idPersona === emp.idPersona);
    return per
      ? `${per.nombre} ${per.apellidoPaterno} ${per.apellidoMaterno || ''}`.trim()
      : `Empleado ${idEmpleado}`;
  };

  // Filtrado
  const filtered = useMemo(() => {
    let data = rows;

    // Filtro de búsqueda
    if (query.trim()) {
      const q = query.toLowerCase().trim();
      data = data.filter((e) => {
        const empNombre = getEmpleadoNombre(e.idEmpleado).toLowerCase();
        const nick = normalizeValue(e.nickname).toLowerCase();
        const avatar = (e.avatarUrl || '').toLowerCase();
        const mods = String(e.modenas ?? '').toLowerCase();
        const acum = String(e.monedasAcumuladas ?? '').toLowerCase();
        const gast = String(e.monedasGastadas ?? '').toLowerCase();
        const notif = (e.notificacionesCursos ? 'si sí' : 'no').toLowerCase();
        return (
          empNombre.includes(q) ||
          nick.includes(q) ||
          avatar.includes(q) ||
          mods.includes(q) ||
          acum.includes(q) ||
          gast.includes(q) ||
          notif.includes(q)
        );
      });
    }

    return data;
  }, [rows, query, empleados, personas]);

  // Paginación
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  // Abrir modal en modo nuevo
  const handleNewStudent = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  // Editar estudiante
  const handleEdit = (id) => {
    const r = rows.find((x) => x.idEstudiante === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  // Abrir diálogo de eliminación
  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  // Confirmar eliminación
  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  // Guardar (nuevo/edición)
  const handleSubmit = async (form, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(form));
        const newId = res?.data?.idEstudiante ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, form));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  // Switch rápido para notificaciones
  const handleToggleNotificaciones = async (estudiante) => {
    const nuevoEstado = !estudiante.notificacionesCursos;
    const payload = maps.mapUpdate(estudiante.idEstudiante, {
      ...estudiante,
      notificacionesCursos: nuevoEstado
    });

    try {
      setLoading(true);
      await axios.put(endpoint, payload);
      await loadData();
    } catch (e) {
      console.error('Error al cambiar estado de notificaciones:', e);
      triggerAlert('No se pudo cambiar el estado de las notificaciones.', 'error', 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="panel-sigma-main">
        <div className="roles-shell">
          <div className="roles-topbar">
            <h2 className="roles-title">{nombreTabla}</h2>

            <input
              className="roles-search"
              placeholder="Buscar por empleado, nickname, avatar, monedas o activo..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />

            <button
              type="button"
              className="btn-action"
              onClick={handleNewStudent}
            >
              ＋ Nuevo
            </button>
          </div>

          {loading ? (
            <div className="catalog-loading">Cargando estudiantes...</div>
          ) : (
            <div className="roles-table-wrap">
              <div className="roles-table-scroller">
                <table className="roles-table">
                  <thead>
                    <tr>
                      <th>Empleado</th>
                      <th>Nickname</th>
                      <th>Monedas</th>
                      <th>Acumuladas</th>
                      <th>Gastadas</th>
                      <th style={{ textAlign: 'center' }}>Notificaciones</th>
                      <th style={{ textAlign: 'center' }}>Editar</th>
                      <th style={{ textAlign: 'center' }}>Borrar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center' }}>
                          No se encontraron estudiantes.
                        </td>
                      </tr>
                    ) : (
                      paginated.map((e) => (
                        <tr key={e.idEstudiante}>
                          <td>{getEmpleadoNombre(e.idEmpleado)}</td>
                          <td>{normalizeValue(e.nickname)}</td>
                          <td>{normalizeValue(e.modenas)}</td>
                          <td>{normalizeValue(e.monedasAcumuladas)}</td>
                          <td>{normalizeValue(e.monedasGastadas)}</td>
                          <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                            <div
                              onClick={() => !loading && handleToggleNotificaciones(e)}
                              style={{
                                width: '38px',
                                height: '20px',
                                backgroundColor: e.notificacionesCursos ? '#10b981' : '#cbd5e1',
                                borderRadius: '12px',
                                position: 'relative',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s',
                                flexShrink: 0,
                                opacity: loading ? 0.6 : 1,
                                margin: '0 auto'
                              }}
                              title={e.notificacionesCursos ? "Desactivar notificaciones" : "Activar notificaciones"}
                            >
                              <div style={{
                                width: '14px',
                                height: '14px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '3px',
                                left: e.notificacionesCursos ? '21px' : '3px',
                                transition: 'left 0.2s',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                              }} />
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className="rk-iconbtn rk-iconbtn--edit"
                              onClick={() => handleEdit(e.idEstudiante)}
                              aria-label="Editar"
                              title="Editar"
                            >
                              <img src={sigmaEdit} alt="" className="rk-ico" />
                            </button>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className="rk-iconbtn rk-iconbtn--del"
                              onClick={() => {
                                setEditingRowId(null);
                                const name = e.nickname || `Estudiante ${e.idEstudiante}`;
                                askDelete(e.idEstudiante, name);
                              }}
                              aria-label="Borrar"
                              title="Borrar"
                            >
                              <img src={sigmaDelete} alt="" className="rk-ico" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="roles-pagination">
            <div className="roles-pagination-left">
              {filtered.length > 0
                ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                : <span>0 resultados</span>}
            </div>
            <div className="roles-pagination-right">
              <PageBtn
                label="‹"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              />
              {pageNumbers.map((n) => (
                <PageBtn
                  key={n}
                  label={n}
                  onClick={() => setPage(n)}
                  active={n === page}
                />
              ))}
              <PageBtn
                label="›"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              />
            </div>
          </div>


          {modalOpen && (
            <div className="roles-editor-slot" ref={editorRef}>
              <Modal
                open={modalOpen}
                mode={modalMode}
                data={editData}
                empleados={empleados}
                personas={personas}
                estudiantes={rows}
                inline
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
              />
              {modalMode === 'edit' && editingRowId > 0 && (
                <EstudiantesTabs idEstudiante={editingRowId} onClose={() => setModalOpen(false)} />
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </>
  );
}
